import { z } from 'zod';
import { MultiPolygonSchema } from './geojson';

/**
 * Comms-degradation estimate zone (#109 — the ice-storm "degraded comms" arc) —
 * served by GET /api/comms-degradation-zones. Backed by the `comms_degradation_zones`
 * table, which the F.1 fusion engine derives by clustering active utility_outages
 * under a cell-tower battery-decay model and corroborating with freezing-rain CAP +
 * the #108 telecom ASN tripwire.
 *
 * HONEST FRAMING (operator-cop-doctrine §2.1 comms/reach-back): this is an
 * ESTIMATE — a buffered outage-cluster envelope, NOT measured coverage, and blind
 * to localized tower loss. Every feature carries `estimate_confidence` + an
 * `estimate_disclaimer`, and the render labels the zone "estimated". The AMBIENT
 * register (§3B.8): a translucent ground-clamped polygon, never pulsing. The
 * `affected_communities` list is the cross-referenced "probably unreachable" set.
 */

/** Battery-decay + corroboration confidence band. */
export const COMMS_ZONE_CONFIDENCES = ['possible', 'probable', 'likely'] as const;
export type CommsZoneConfidence = (typeof COMMS_ZONE_CONFIDENCES)[number];

/** A W.1 community that falls inside the estimated zone (probably unreachable). */
export const AffectedCommunitySchema = z.object({
  id: z.string(),
  name: z.string(),
  population: z.number().nullable(),
  vulnerable_population: z.number().nullable(),
});
export type AffectedCommunity = z.infer<typeof AffectedCommunitySchema>;

export const CommsDegradationZonePropertiesSchema = z.object({
  id: z.string().uuid(),
  kind: z.literal('comms_degradation'),
  status: z.string(),
  /** 'possible' (<2 h) | 'probable' (2–8 h) | 'likely' (≥8 h), bumped by corroboration. */
  estimate_confidence: z.string(),
  /** Always-present honesty string for the panel. */
  estimate_disclaimer: z.string(),
  /** Estimated zone area (km²), rounded. */
  area_km2: z.number().nullable(),
  outage_count: z.number(),
  customers_affected: z.number().nullable(),
  utilities: z.array(z.string()),
  /** Hours since the earliest outage in the cluster (battery-decay basis). */
  elapsed_hours: z.number().nullable(),
  has_freezing_rain: z.boolean(),
  has_asn_tripwire: z.boolean(),
  /** Carriers whose ASN tripped (#108): 'Rogers' | 'Bell' | 'Telus'. */
  asn_carriers: z.array(z.string()),
  communities_affected: z.number(),
  /** The "probably unreachable" community list. */
  affected_communities: z.array(AffectedCommunitySchema),
  first_detected_at: z.string(),
  last_seen_at: z.string(),
  created_at: z.string(),
});
export type CommsDegradationZoneProperties = z.infer<typeof CommsDegradationZonePropertiesSchema>;

/**
 * The primary geometry is the estimated zone MultiPolygon (the buffered outage
 * cluster). `centroid` rides alongside as a [lon, lat] tuple for label placement /
 * fly-to without re-deriving it from the polygon.
 */
export const CommsDegradationZoneFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: MultiPolygonSchema,
  centroid: z.tuple([z.number(), z.number()]),
  properties: CommsDegradationZonePropertiesSchema,
});
export type CommsDegradationZoneFeature = z.infer<typeof CommsDegradationZoneFeatureSchema>;

export const CommsDegradationZoneFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(CommsDegradationZoneFeatureSchema),
});
export type CommsDegradationZoneFeatureCollection = z.infer<
  typeof CommsDegradationZoneFeatureCollectionSchema
>;
